import clsx from 'clsx'
import { forwardRef, cloneElement, type ButtonHTMLAttributes, type ReactElement, type HTMLAttributes } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CheckCircle, XCircle } from '../../assets/icons/index.ts'
import { Tooltip, type TooltipProps } from '@/components/ui/tooltip/Tooltip'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES, ICON_BUTTON_COLORS } from './constants'
import type { IconButtonColor, IconButtonLoadingState, IconButtonSize } from './types'

const spinnerTheme = createTheme({
  palette: {
    primary: {
      main: '#fff',
    },
  },
})

// ============================================================================
// STYLE DEFINITIONS
// ============================================================================

/**
 * Style definitions per variant
 * Note: Only plain variant supports multiple colors
 */
const variantStyles = {
  filled: clsx(
    'bg-action-filled-surface-brand text-action-filled-text shadow-xs',
    'hover:bg-action-filled-surface-hover-brand active:bg-action-filled-surface-hover-brand',
    'focus-visible:outline-brand-500',
    'disabled:bg-action-filled-surface-disabled disabled:text-action-filled-text disabled:shadow-none',
    '[&[data-loading-state="true"]]:bg-action-filled-surface-hover-brand [&[data-loading-state="true"]]:text-action-filled-text'
  ),
  outlined: clsx(
    'bg-transparent text-action-outlined-text-brand',
    'ring-1 ring-inset ring-action-outlined-border-brand',
    'hover:bg-surface-hover active:bg-surface-hover',
    'focus-visible:outline-brand-500',
    'disabled:text-action-outlined-text-disabled disabled:ring-action-outlined-border-disabled disabled:bg-transparent',
    '[&[data-loading-state="true"]]:bg-surface-hover [&[data-loading-state="true"]]:text-action-outlined-text-brand [&[data-loading-state="true"]]:ring-action-outlined-border-brand'
  ),
  tonal: clsx(
    'bg-action-tonal-surface-neutral text-action-tonal-text-neutral',
    'ring-1 ring-inset ring-action-tonal-border-neutral',
    'hover:bg-action-tonal-surface-hover-neutral active:bg-action-tonal-surface-hover-neutral',
    'focus-visible:outline-brand-neutral-500',
    'disabled:bg-action-tonal-surface-disabled disabled:text-action-tonal-text-disabled disabled:ring-action-tonal-border-disabled',
    '[&[data-loading-state="true"]]:bg-action-tonal-surface-hover-neutral [&[data-loading-state="true"]]:text-action-tonal-text-neutral [&[data-loading-state="true"]]:ring-action-tonal-border-neutral'
  ),
} as const

const plainColorStyles = {
  primary: clsx(
    'text-action-plain-text-brand',
    'hover:bg-action-plain-surface-hover-brand active:bg-action-plain-surface-hover-brand',
    'focus-visible:outline-brand-500',
    'disabled:text-action-plain-text-disabled disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-action-plain-text-brand'
  ),
  neutral: clsx(
    'text-action-plain-text-neutral',
    'hover:bg-action-plain-surface-hover-neutral active:bg-action-plain-surface-hover-neutral',
    'focus-visible:outline-brand-neutral-500',
    'disabled:text-action-plain-text-disabled disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-action-plain-text-neutral'
  ),
  black: clsx(
    'text-action-plain-text-secondary',
    'hover:bg-action-plain-surface-hover-secondary active:bg-action-plain-surface-hover-secondary',
    'focus-visible:outline-brand-neutral-900',
    'disabled:text-action-plain-text-disabled disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-action-plain-text-secondary'
  ),
  // Matches the plain button's `destructive` so a labelled and an icon-only Delete read as the same
  // action at different widths.
  destructive: clsx(
    'text-action-plain-text-destructive',
    'hover:bg-action-plain-surface-hover-destructive active:bg-action-plain-surface-hover-destructive',
    'focus-visible:outline-red-700',
    'disabled:text-action-plain-text-disabled disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-action-plain-text-destructive'
  ),
} as const

/**
 * Size styles for filled, outlined, and tonal variants
 */
const iconButtonSizeStyles: Record<IconButtonSize, string> = {
  xs: 'h-[24px] w-[24px] rounded p-1',
  sm: 'h-[28px] w-[28px] rounded-md p-1',
  md: 'h-[36px] w-[36px] rounded-md p-1.5',
  lg: 'h-[40px] w-[40px] rounded-md p-2',
}

/**
 * Wrapper sizes for plain icon buttons
 * Plain variant uses a wrapper div for consistent layout sizing
 * xs is not available for plain variant
 */
const plainIconOuterSizes: Record<Exclude<IconButtonSize, 'xs'>, string> = {
  sm: 'h-[28px] w-[28px]',
  md: 'h-[36px] w-[36px]',
  lg: 'h-[40px] w-[40px]',
}

/**
 * Icon dimensions per button size
 */
const iconSizeByButtonSize: Record<IconButtonSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
}

/**
 * Spinner pixel size per button size
 */
const spinnerSizeByButtonSize: Record<IconButtonSize, number> = {
  xs: 13,
  sm: 13,
  md: 16,
  lg: 18,
}

/**
 * Base styles applied to all icon buttons
 */
const baseStyles = clsx(
  'inline-flex items-center justify-center font-medium transition-colors duration-200',
  'focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2',
  'disabled:cursor-not-allowed'
)

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Shared icon button props (used by all variants)
 * Note: aria-label is required per WCAG 4.1.2 - icon-only buttons must have an accessible name.
 */
interface SharedIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'children'> {
  /**
   * Accessible name for the button (required).
   * Icon-only buttons have no visible text, so an aria-label is required for screen reader users.
   */
  'aria-label': string
  /**
   * Icon to display (required)
   */
  icon: ReactElement
  /**
   * The size of the icon button
   * @default 'md'
   */
  size?: IconButtonSize
  /**
   * Loading
   * - true or 'loading': Shows spinner and disables button
   * - 'success': Shows checkmark icon
   * - 'error': Shows X icon
   * - false/undefined: Shows normal icon
   * @default false
   */
  loading?: IconButtonLoadingState
  /**
   * Optional text shown in a tooltip on hover. Omit to render the button with no tooltip.
   * Skipped on touch-only devices (where a tap would toggle the tooltip instead of firing the button).
   * The `aria-label` is still the accessible name — the tooltip is a visual affordance only.
   */
  tooltipText?: string
  /**
   * Preferred tooltip placement. Popper auto-flips to the opposite side near the viewport edge.
   * @default 'top'
   */
  tooltipPlacement?: TooltipProps['placement']
  /**
   * Force the tooltip open regardless of hover/touch. Storybook/Chromatic and testing only.
   * @default false
   */
  forceTooltipOpen?: boolean
  /**
   * Render the tooltip bubble non-interactive so it never captures the cursor or steals the
   * button's hover state.
   * @default false
   */
  disableTooltipInteractive?: boolean
  /**
   * Open the tooltip on hover only.
   * @default false
   */
  disableTooltipFocusListener?: boolean
  /**
   * Milliseconds to wait before opening the tooltip on hover.
   */
  tooltipHoverDelayMs?: number
}

/**
 * Props for filled/outlined/tonal variants (no color prop)
 */
type FilledOutlinedTonalProps = SharedIconButtonProps & {
  variant?: 'filled' | 'outlined' | 'tonal'
  color?: never
  wrapperClassName?: never
}

/**
 * Props for plain variant (color optional, size excludes xs)
 * Plain renders a wrapper div around the button so the hover circle can sit inside a larger
 * layout footprint — wrapperClassName styles that outer div (for positioning, margins, etc.).
 */
type PlainProps = Omit<SharedIconButtonProps, 'size'> & {
  variant: 'plain'
  color?: IconButtonColor
  size?: Exclude<IconButtonSize, 'xs'>
  wrapperClassName?: string
}

/**
 * Props for default (no variant specified, defaults to filled)
 */
type DefaultProps = SharedIconButtonProps & {
  variant?: never
  color?: never
  wrapperClassName?: never
}

export type IconButtonProps = FilledOutlinedTonalProps | PlainProps | DefaultProps

/**
 * IconButton Component
 *
 * A square icon-only button component following the Paxton design system.
 *
 * Variants:
 * - filled: Blue background (default)
 * - outlined: Blue border with transparent background
 * - tonal: Neutral background
 * - plain: No background - supports color prop (primary, neutral, black, destructive)
 *
 * @example
 * // Primary action (filled)
 * <IconButton icon={<PlusIcon />} aria-label="Add" />
 *
 * @example
 * // Secondary action (outlined)
 * <IconButton icon={<EditIcon />} variant="outlined" aria-label="Edit" />
 *
 * @example
 * // Subtle action (tonal)
 * <IconButton icon={<SettingsIcon />} variant="tonal" aria-label="Settings" />
 *
 * @example
 * // Plain style with colors
 * <IconButton icon={<XIcon />} variant="plain" color="primary" aria-label="Close" />
 * <IconButton icon={<XIcon />} variant="plain" color="neutral" aria-label="Close" />
 * <IconButton icon={<XIcon />} variant="plain" color="black" aria-label="Close" />
 * <IconButton icon={<TrashIcon />} variant="plain" color="destructive" aria-label="Delete" />
 *
 * @example
 * // Different sizes (xs only available for filled/outlined/tonal)
 * <IconButton icon={<SettingsIcon />} size="xs" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="sm" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="md" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="lg" aria-label="Settings" />
 *
 * @example
 * // Override icon size for special cases
 * <IconButton icon={<CustomIcon className="h-5 w-5" />} size="sm" aria-label="Custom" />
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    className,
    color,
    disabled,
    icon,
    loading = false,
    size = ICON_BUTTON_SIZES.MD,
    variant = ICON_BUTTON_VARIANTS.FILLED,
    wrapperClassName,
    tooltipText,
    tooltipPlacement = 'top',
    forceTooltipOpen = false,
    disableTooltipInteractive = false,
    disableTooltipFocusListener = false,
    tooltipHoverDelayMs,
    ...props
  },
  ref
) {
  // On touch-only devices the tooltip toggles on tap, which competes with the button's action,
  // so we skip it there.
  const isTouchOnly = useIsTouchOnlyDevice()
  const isLoading = loading === true || loading === 'loading'
  const isSuccess = loading === 'success'
  const isError = loading === 'error'
  const isDisabled = disabled || isLoading

  // Add data attribute for loading/success/error states
  const hasLoadingState = isLoading || isSuccess || isError
  const buttonProps = {
    ...props,
    ...(hasLoadingState && { 'data-loading-state': 'true' }),
  }

  const iconSize = iconSizeByButtonSize[size]

  // Render loading/success/error icon
  const renderStateIcon = () => {
    if (isLoading) {
      const spinnerSize = spinnerSizeByButtonSize[size]

      return (
        <span className={clsx('inline-flex items-center justify-center text-current', iconSize)} aria-hidden>
          <ThemeProvider theme={spinnerTheme}>
            <CircularProgress size={spinnerSize} thickness={7} sx={{ color: 'currentColor' }} />
          </ThemeProvider>
        </span>
      )
    }

    if (isSuccess) return <CheckCircle className={iconSize} aria-hidden />

    if (isError) return <XCircle className={iconSize} aria-hidden />

    return null
  }

  // Apply size classes directly to the icon element
  const iconWithSize = loading
    ? renderStateIcon()
    : cloneElement(icon, {
        className: clsx(iconSize, icon.props.className),
        'aria-hidden': true,
      } as Pick<HTMLAttributes<HTMLElement>, 'className' | 'aria-hidden'>)

  // Only `plain` accepts a configurable color; other variants encode color into the variant itself.
  const dsAttributes = {
    'data-ds-component': 'IconButton',
    'data-ds-variant': variant,
    'data-ds-size': size,
    ...(variant === ICON_BUTTON_VARIANTS.PLAIN && {
      'data-ds-color': color || ICON_BUTTON_COLORS.PRIMARY,
    }),
  }

  // Build the button element once, then wrap it in a single Tooltip below.
  let buttonElement: ReactElement
  if (variant === ICON_BUTTON_VARIANTS.PLAIN) {
    // Plain variant uses a wrapper for layout + circular button
    const plainColor = color || ICON_BUTTON_COLORS.PRIMARY
    const plainSize = size as Exclude<IconButtonSize, 'xs'>

    buttonElement = (
      <div
        data-testid="icon-button-wrapper"
        {...dsAttributes}
        className={clsx(plainIconOuterSizes[plainSize], 'inline-flex items-center justify-center', wrapperClassName)}
      >
        <button ref={ref} className={clsx(baseStyles, plainColorStyles[plainColor], 'rounded-full p-1', className)} disabled={isDisabled} {...buttonProps}>
          {iconWithSize}
        </button>
      </div>
    )
  } else {
    // Other variants (filled, outlined, tonal) use single-layer structure
    buttonElement = (
      <button
        ref={ref}
        {...dsAttributes}
        className={clsx(baseStyles, variantStyles[variant], iconButtonSizeStyles[size], className)}
        disabled={isDisabled}
        {...buttonProps}
      >
        {iconWithSize}
      </button>
    )
  }

  // Only wrap in a Tooltip when we actually intend to show one — avoids mounting a Tooltip (and its
  // matchMedia subscription) on every icon button. Skipped when disabled/loading, since disabled
  // elements don't fire hover events (and MUI warns about tooltips on them).
  const showTooltip = !!tooltipText && !isDisabled && (!isTouchOnly || forceTooltipOpen)

  if (!showTooltip) return buttonElement

  return (
    <Tooltip
      title={tooltipText ?? ''}
      placement={tooltipPlacement}
      offsetY={-10}
      forceOpen={forceTooltipOpen}
      disableInteractive={disableTooltipInteractive}
      disableFocusListener={disableTooltipFocusListener}
      hoverDelayMs={tooltipHoverDelayMs}
    >
      {buttonElement}
    </Tooltip>
  )
})

export { IconButton }
